import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { UserRole } from '@etnrlz/shared';
import { config } from '../config';
import { setTokenProvider } from '../lib/api';

export interface AuthUser {
  nome: string;
  email?: string;
  role: UserRole;
  groups: string[];
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  ready: boolean;
  login: () => void;
  logout: () => void;
}

const AuthCtx = createContext<AuthState | null>(null);

const DEV_USER: AuthUser = {
  nome: 'Celso (dev)',
  email: 'celso@etnrlz.com',
  role: 'ADMIN',
  groups: ['ADMIN'],
};

function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    const bin = atob(padded);
    // Decodifica UTF-8 corretamente (nomes com acento etc.).
    const json = decodeURIComponent(
      Array.from(bin, (c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(''),
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Captura o `id_token` do fragmento de URL IMEDIATAMENTE no carregamento do
 * módulo — antes de qualquer render/roteamento. Sem isso, o `<Navigate>` do
 * React Router roda seu efeito (filho) antes do efeito do AuthProvider (pai) e
 * faz `history.replace`, apagando o hash antes de conseguirmos lê-lo.
 */
function captureHashTokenOnLoad(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.hash.slice(1));
  const token = params.get('id_token');
  if (token) {
    localStorage.setItem('id_token', token);
    window.history.replaceState({}, '', window.location.pathname + window.location.search);
  }
}
captureHashTokenOnLoad();

function roleFromGroups(groups: string[]): UserRole {
  const order: UserRole[] = ['ADMIN', 'GERENTE', 'MECANICO', 'VISUALIZADOR'];
  return order.find((r) => groups.includes(r)) ?? 'VISUALIZADOR';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setTokenProvider(() => token);
  }, [token]);

  useEffect(() => {
    if (config.authMode === 'dev') {
      setUser(DEV_USER);
      setReady(true);
      return;
    }
    // O token do hash já foi capturado em captureHashTokenOnLoad() (módulo).
    const stored = localStorage.getItem('id_token');
    if (stored) {
      const claims = decodeJwt(stored);
      if (claims && (claims.exp as number) * 1000 > Date.now()) {
        const groups = (claims['cognito:groups'] as string[] | undefined) ?? [];
        setToken(stored);
        setUser({
          nome: (claims.name as string) ?? (claims.email as string) ?? 'Usuário',
          email: claims.email as string | undefined,
          role: roleFromGroups(groups),
          groups,
        });
      } else {
        localStorage.removeItem('id_token');
      }
    }
    setReady(true);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      token,
      ready,
      login: () => {
        const { domain, clientId, redirectUri } = config.cognito;
        if (!domain || !clientId) {
           
          console.warn('Cognito não configurado (VITE_COGNITO_*).');
          return;
        }
        const url = `https://${domain}/login?client_id=${clientId}&response_type=token&scope=openid+email+profile&redirect_uri=${encodeURIComponent(redirectUri ?? '')}`;
        window.location.href = url;
      },
      logout: () => {
        localStorage.removeItem('id_token');
        setToken(null);
        setUser(null);
        const { domain, clientId, redirectUri } = config.cognito;
        if (config.authMode === 'cognito' && domain && clientId) {
          window.location.href = `https://${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(redirectUri ?? '')}`;
        }
      },
    }),
    [user, token, ready],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}

/** Permissões simples por papel. */
export function canEdit(role: UserRole | undefined): boolean {
  return role === 'ADMIN' || role === 'GERENTE' || role === 'MECANICO';
}
export function canManage(role: UserRole | undefined): boolean {
  return role === 'ADMIN' || role === 'GERENTE';
}
